import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import JuanEstebanPhoto from "../candidates/santiago.jpg"

export default function SocialMediaCreativeStrategistSantiago() {
  const candidateProfileCopy = {
    name: "Santiago",
    location: "Bucaramanga, Colombia",
    compensation: {
      range: "$18 USD / hourly",
    },
    availability: "Available to start immediately",

    working_style:
      "Santiago is a metric-driven social media operator who thinks in conversion outcomes first and content formats second. He naturally orients strategy around email capture and subscriber growth, building DM lead funnels, implementing ManyChat automation workflows, and embedding CTAs into content architecture from the start. He produces weekly KPI reports, optimizes content themes based on performance data, and has delivered specific, measurable growth results across multiple roles. His AI adoption is notably proactive for his experience level, including training custom AI agents with brand voice and market data. He is the youngest candidate in this group, but his instincts, metric accountability, and operational habits are ahead of his experience level.",

    loom_video_link: "https://fathom.video/share/ez94Mc8gzy_toYcb1WyFLZgthChjxXsg",

    tools: [
      {
        category: "Content Strategy & Subscriber Conversion",
        items: [
          "Full-funnel content architecture (top/middle/bottom) with CTA-first planning oriented toward email capture",
          "Lead magnet and gated content strategy, value resources exchanged for email sign-ups",
          "ManyChat automation: DM lead funnels, keyword triggers, routing, and AI-powered responses",
          "Content calendar management in 2-week planning cycles with platform and funnel stage mapping",
        ],
      },
      {
        category: "Platforms & Publishing Ops",
        items: [
          "Instagram and TikTok (daily operations: content planning, publishing, brand voice consistency)",
          "Meta Business Suite and Meta Ads (scheduling, analytics, campaign management)",
          "YouTube Shorts (short-form video publishing and optimization)",
          "Metricool, Hootsuite (cross-platform scheduling, analytics, and performance reporting)",
          "Google Analytics (performance tracking and audience behavior analysis)",
        ],
      },
      {
        category: "Content Production",
        items: [
          "CapCut (short-form video editing for TikTok, Reels, Shorts)",
          "Canva (social media graphics, branded visuals, content assets)",
          "Hook-first video structure: visual, text, and sound hooks for first-2-second retention",
          "Weekly KPI reporting with content theme optimization based on retention and reach data",
        ],
      },
      {
        category: "AI Tools & Automation",
        items: [
          "Custom AI agent trained with market data, brand voice, and content preferences",
          "Gemini (integrated with Google Workspace for daily operations)",
          "ManyChat with AI-powered personalized Instagram DM responses",
          "Claude (content support and strategic research)",
        ],
      },
    ],

    soft_skills: [
      "Metric-accountable: delivers weekly KPI reports and optimizes based on data, not intuition",
      "Conversion-first thinker: orients strategy around email capture and subscriber growth unprompted",
      "Specific, verifiable growth results: +406% views, +544% reach, +32% follower growth across roles",
      "Proactive AI adopter: trains custom agents and builds automation into daily workflows",
      "Self-directed and organized: owns content calendars and publishing operations independently",
      "Bilingual (English C1 / Spanish native) with professional certification",
    ],

    about_candidate:
      "Santiago is the youngest candidate in this group and the most metric-driven. He delivered +406% views, +544% reach, and +439% interactions in 6 months at WE International Studies, and +32% follower growth in 3 months at Club Union, all tied directly to his own content strategy and execution. He naturally orients strategy around email conversion and subscriber growth, and brings ManyChat lead funnel automation experience that adds a practical conversion infrastructure layer to the content operation. His metric accountability, conversion-first instincts, and proactive AI adoption are ahead of his experience level, and at $18 per hour he offers the strongest cost-to-potential ratio in this group.",

    core_strengths: [
      {
        headline: "Most metric-evidenced candidate in this group",
        description:
          "The only candidate who brought specific before/after growth numbers tied directly to his own work: +406% views, +544% reach, +439% interactions in 6 months, and +32% follower growth in 3 months. Produces weekly KPI reports and optimizes content themes based on data. In a role defined by three metrics, his habit of quantifying and acting on results is the most demonstrated here.",
      },
      {
        headline: "Natural content-to-subscriber conversion instinct with automation capability",
        description:
          "Oriented his entire strategy around email capture from his first interview answer, before being asked about it. Combined with ManyChat automation experience (DM funnels, keyword triggers, lead routing, AI-powered responses), he brings both the strategic mindset and the technical tooling to build the subscriber pipeline.",
      },
      {
        headline: "ManyChat lead funnel automation, unique in this candidate group",
        description:
          "For a role where every high-intent post needs a tested path to the email list, someone who can build the actual conversion infrastructure (DM automation, keyword triggers, inbound routing) directly serves the #1 metric. This is a practical capability that adds real operational value to the content operation.",
      },
      {
        headline: "Strongest cost-to-potential ratio in the group",
        description:
          "At $18 per hour, Santiago combines metric-driven execution habits, conversion-first strategic thinking, ManyChat automation capability, and proactive AI adoption including training custom AI agents. His demonstrated results show he can move the metrics the role measures, even as his production quality and experience depth continue developing.",
      },
    ],

    relevant_experience: [
      {
        headline: "Social Media Coordinator | WE International Studies | June 2025 to January 2026",
        bullets: [
          "Managed daily Instagram and TikTok operations: content planning, publishing, and brand voice consistency",
          "Organized editorial calendar and coordinated assets and edits across multiple formats on schedule",
          "Produced weekly KPI reports (views, reach, interactions) and optimized content themes, contributing to +406% views, +544% reach, and +439% interactions in 6 months",
          "Researched trends and audience behavior to improve hooks, messaging, and content pillars",
          "Built a DM lead funnel and implemented ManyChat workflows (keyword triggers, routing) to improve inbound handling and response speed",
        ],
      },
      {
        headline: "Marketing Intern | Club Union | January 2024 to July 2024",
        bullets: [
          "Supported Facebook, Instagram, and WhatsApp content scheduling, monitoring, and community management",
          "Assisted in planning and executing marketing campaigns for company events",
          "Conducted audience segmentation and built customer personas to guide targeting and messaging",
          "Tracked results and improved outcomes year-over-year: +110% reach, +199% engagement, +32% follower growth in 3 months",
        ],
      },
      {
        headline: "Digital Marketing Consultant (Independent) | December 2024 to Present",
        bullets: [
          "Created brand identity systems (logo, typography, color palette, collateral) for client consistency",
          "Supported organic growth using Google Analytics, Search Console, SEMrush, and Ubersuggest",
          "Collaborated with developers on SEO implementation and web optimization",
        ],
      },
    ],
  };

  const technicalAssessment = {
    meta: {
      role: "Social Media Creative Strategist, Frizo / LGO Brands",
      interviewDate: "March 16, 2026",
      analyst: "AI-Architechs Senior Technical Hiring Analysis",
    },

    snapshot:
      "Santiago is the youngest candidate in this group and the most metric-driven. He's the only one who brought specific before/after growth numbers tied directly to his own work, and he oriented his strategy around email conversion from his first answer without being prompted. He also brings ManyChat lead funnel automation that adds a practical conversion infrastructure layer no other candidate offers. He's earlier in his career than the role ideally targets, and his production tools are functional but still developing. He is the highest-upside candidate at his price point, with the right instincts and a strong growth trajectory.",

    scorecard: [
      {
        dimension: "Content Production & Visual Identity",
        score: 5,
        rationale:
          "Managed daily Instagram and TikTok operations including content planning, publishing, and brand voice consistency. Uses CapCut for video editing and Canva for design, which are functional tools that have driven strong growth metrics in his previous roles. His production toolkit doesn't yet include Adobe Suite or more advanced editing tools, so building a premium, atmospheric visual style across multiple brands would be a growth area for him. His content has clearly performed well by the numbers, and the question is whether his visual output matches the aesthetic bar Chad envisions for Frizo and LGO.",
      },
      {
        dimension: "Content-to-Subscriber Pipeline Thinking",
        score: 8,
        rationale:
          "The second-strongest pipeline thinker in this group. In his very first interview answer, unprompted, he oriented his entire strategy around automations that lead to email capture. Described lead magnets as gated value resources requiring email sign-up. Has built functional DM lead funnels with ManyChat including keyword triggers, routing, and inbound handling. Thinks in conversion channels and automation mechanics, not just content formats.",
      },
      {
        dimension: "Full-Funnel Content Strategy",
        score: 7,
        rationale:
          "Clearly articulated top, middle, and bottom of funnel with appropriate content types for each layer. Made a sophisticated observation that the algorithm doesn't respect your content calendar's funnel labels, meaning you can't guarantee a video will serve as top-of-funnel content. This shows genuine platform experience rather than theoretical framework knowledge. Proposed 2-week content planning cycles and demonstrated real operational understanding of how funnel strategy plays out in practice.",
      },
      {
        dimension: "Scripting, Hook Writing & Copywriting",
        score: 6,
        rationale:
          "Described three types of hooks (visual, text, and sound) and explained their importance for first-2-second retention and algorithmic distribution. Understands hook mechanics at a structural level. He didn't walk through concrete hook examples for a product brand context in the interview, and email copywriting wasn't part of his experience to date. The framework knowledge is directionally strong, and his understanding of what makes hooks work is solid, though this is an area where his depth is still building.",
      },
      {
        dimension: "Founder Coaching & On-Camera Direction",
        score: 7,
        rationale:
          "Framed his role clearly as making the founder's life easier and proposed a realistic 2 to 4 hour weekly filming commitment. Emphasized humanizing the brand through the founder's personal story, background, values, and mission. Understands that the founder is the raw material and the strategist's job is to extract and package that authentically. His philosophy and instinct here are well-aligned with what Chad described wanting from this role.",
      },
      {
        dimension: "Competitive Research & Cross-Category Intelligence",
        score: 6,
        rationale:
          "Uses a dedicated ghost account on Instagram and TikTok to follow competitors and big creators across the niche, which is a practical method that shows this is a real operational habit for him. Mentioned adapting viral ideas from large creators into specific content niches. Referenced software tools for trend research but didn't name specifics. Functional and consistent approach, though less systematic than candidates who described organized tracking tools and databases.",
      },
      {
        dimension: "Metrics Accountability & Performance Optimization",
        score: 8,
        rationale:
          "The most metric-evidenced candidate in this group. Specific, measurable outcomes across two roles: +406% views, +544% reach, +439% interactions in 6 months; +110% reach, +199% engagement, +32% follower growth in 3 months. Produced weekly KPI reports and optimized content themes based on that data. Gave a concrete optimization example: re-edited a video that improved retention from around 50% to 70 to 80%, with reach tripling as a result. This is a demonstrated data-driven feedback loop, not just claimed methodology.",
      },
      {
        dimension: "Regulated/Restricted-Ad Industry Readiness",
        score: 5,
        rationale:
          "No direct experience in restricted-ad industries yet. Proposed myth-busting content and using customer feedback to break down misconceptions, which are valid strategies. Drew an analogy to course sellers fighting scam perceptions, showing the right instinct for trust-building educational content. He'd be learning the hemp and cannabis compliance landscape on the job, but his instinct for educational, trust-building content is a good starting point.",
      },
      {
        dimension: "AI Tools & Workflow Efficiency",
        score: 7,
        rationale:
          "Notably proactive for his experience level. Trained a custom AI agent with market data, brand voice, and content preferences, which goes a step beyond standard tool usage. Uses Gemini integrated with Google Workspace for daily operations, ManyChat with AI-powered personalized Instagram responses, and Claude. Shows genuine initiative in building practical AI systems into his workflow rather than just listing tools he's tried.",
      },
    ],

    strengths: [
      {
        headline: "Most metric-accountable candidate in this group with verifiable evidence",
        description:
          "The only candidate who brought exact before/after growth numbers tied directly to his own work across multiple roles. In a role defined by three metrics, someone who already produces weekly KPI reports, tracks performance systematically, and has demonstrated real outcomes shows the operating pattern this role needs: measurement, diagnosis, iteration.",
      },
      {
        headline: "Natural content-to-subscriber conversion instinct with automation tooling",
        description:
          "Oriented his entire strategy around email capture from his first answer, before being asked about it. Combined with ManyChat automation experience (DM funnels, keyword triggers, lead routing, AI-powered responses), he brings both the strategic mindset and the technical infrastructure to build the subscriber pipeline.",
      },
      {
        headline: "ManyChat lead funnel automation is unique and directly relevant",
        description:
          "For a role where every high-intent post needs a tested path to the email list, someone who can build the actual conversion infrastructure (DM automation, keyword triggers, inbound routing) directly serves the #1 metric. This is a practical, operational capability that adds real value to the content operation.",
      },
      {
        headline: "Sophisticated platform understanding beyond theoretical frameworks",
        description:
          "Made an observation most candidates missed: the algorithm doesn't respect your content calendar's funnel labels, and you can't guarantee how a piece of content will be distributed. This shows someone who has actually operated on these platforms and understands how content distribution really works in practice.",
      },
      {
        headline: "Proactive AI adoption with custom agent training",
        description:
          "Trained a custom AI agent with market data, brand voice, and content preferences, going beyond standard tool usage to build AI into his actual operational workflow. Combined with ManyChat AI integration for Instagram DMs, this shows an automation-forward mindset aligned with the role's expectation of using AI tools and efficient workflows to replace what would normally require a larger team.",
      },
    ],

    flags: [
      {
        headline: "Production toolkit is still developing",
        description:
          "Santiago's current tools (CapCut and Canva) have driven strong growth metrics, but the role calls for a visual-first, product-forward, atmospheric editing style across multiple brands. His toolkit doesn't yet include Adobe Suite or more advanced production tools. His content has clearly performed well by the numbers, so the question is whether his visual output matches the aesthetic level Chad has in mind for Frizo and LGO. Worth exploring in conversation by looking at examples of his recent work.",
      },
      {
        headline: "Youngest candidate with less career depth than others in the group",
        description:
          "Santiago has about 2 to 3 years of professional social media experience, while the role description targets a senior creative strategist. He would be growing into some aspects of the role (brand voice development, founder coaching, email copywriting) rather than bringing deep experience from day one. His trajectory and results suggest he can grow quickly, and at $18 per hour the investment has strong upside, but it's worth considering how much ramp-up time is realistic.",
      },
      {
        headline: "Interview answers were directionally strong but stayed high-level",
        description:
          "His responses showed the right instincts and frameworks, but were consistently shorter and less detailed than some of the more experienced candidates on topics like hook writing specifics, content format design, and funnel-layer strategy. This could reflect his concise communication style or the fact that his execution depth is still building. A conversation with Chad would help clarify how deep his thinking goes when given the chance to elaborate.",
      },
    ],

    when_to_hire: [
      "You're open to a high-upside candidate who can grow into the role and offers the strongest cost-to-potential ratio in the group.",
      "Metric-driven execution and content-to-subscriber pipeline thinking are weighted more heavily than premium production quality from day one.",
      "The ManyChat and automation skillset is valued as a practical differentiator for building the email conversion infrastructure alongside the content operation.",
      "You see his growth trajectory and metric results as evidence that he'll rise to the scope of the role quickly.",
    ],

    when_to_be_cautious: [
      "You need a senior creative strategist who can operate at full scope from day one without significant ramp-up.",
      "Premium visual identity and cinematic-quality short-form content are immediate requirements.",
      "Scripting, hook writing, and email copywriting demands are heavy from the start with limited room for on-the-job learning.",
      "Experience depth and career maturity are weighted heavily in the hiring decision.",
    ],

    bottom_line:
      "Santiago is the #3 candidate in this group, and the highest-upside option with the strongest metric evidence and the most relevant conversion automation skills. His instincts are ahead of his experience level, his results are the most specifically quantified in this group, and his ManyChat automation capability adds practical conversion infrastructure that no other candidate brings. He's the youngest candidate and would be growing into some aspects of the role, but his trajectory suggests he can get there quickly. If Chad values metric-driven execution and conversion-first thinking, and is open to investing in someone with strong momentum, Santiago is worth a serious conversation.",
  };

  // Score pill helper
  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <img
              src={JuanEstebanPhoto}
              alt={candidateProfileCopy.name}
              className="relative w-36 h-36 rounded-full object-cover border-4 border-[#36D1FF] shadow-lg"
            />
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Video Intro */}
          {candidateProfileCopy.loom_video_link && (
            <div className="w-full mt-4">
              <a
                href={candidateProfileCopy.loom_video_link}
                target="_blank"
                rel="noreferrer"
                className="block w-full text-center font-semibold py-3 rounded-lg
                           bg-[#36D1FF] text-[#01014C]
                           hover:bg-[#5FE2FF]
                           transition-all duration-300"
              >
                🎥 Watch Interview
              </a>
            </div>
          )}


          {/* Tools */}
          <div className="w-full mt-8 mb-6 text-left">
            <h3 className="text-sm font-semibold mb-3 text-[#B3E9FF] uppercase">
              Tools
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-4">
                <p className="text-xs font-semibold text-[#36D1FF] mb-1 uppercase tracking-wide">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((tool, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-xs text-[#E0F7FF]/90 leading-snug"
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#36D1FF] shrink-0" />
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Soft Skills
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI-Architechs Logo" className="w-42 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* Strengths */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flags */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">
                          {row.dimension}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ScorePill score={row.score} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">
                          {row.rationale}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Considerations */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* When to Hire */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire Juan Esteban if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* When to Be Cautious */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}
